import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

const STORAGE_KEY = 'drywall_cart_v1';

const CartContext = createContext(null);

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
};

const loadFromStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { items: [], currency: 'usd' };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return { items: [], currency: 'usd' };
    return { items: parsed.items, currency: parsed.currency === 'eur' ? 'eur' : 'usd' };
  } catch {
    return { items: [], currency: 'usd' };
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => loadFromStorage().items);
  const [currency, setCurrency] = useState(() => loadFromStorage().currency);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ items, currency }));
  }, [items, currency]);

  const addItem = useCallback((tool, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.id === tool.id);
      if (existing) {
        return prev.map((i) =>
          i.id === tool.id ? { ...i, quantity: Math.min(99, i.quantity + qty) } : i
        );
      }
      return [
        ...prev,
        {
          id: tool.id,
          name: tool.name,
          image: tool.image,
          price_usd: tool.price_usd,
          price_eur: tool.price_eur,
          quantity: qty,
        },
      ];
    });
  }, []);

  const updateQty = useCallback((toolId, qty) => {
    setItems((prev) =>
      prev
        .map((i) => (i.id === toolId ? { ...i, quantity: Math.max(1, Math.min(99, qty)) } : i))
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const removeItem = useCallback((toolId) => {
    setItems((prev) => prev.filter((i) => i.id !== toolId));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const priceField = currency === 'eur' ? 'price_eur' : 'price_usd';
  const symbol = currency === 'eur' ? '€' : '$';
  const subtotal = items.reduce((sum, i) => sum + i[priceField] * i.quantity, 0);
  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);

  const value = useMemo(
    () => ({
      items,
      currency,
      setCurrency,
      symbol,
      subtotal,
      totalItems,
      addItem,
      updateQty,
      removeItem,
      clearCart,
    }),
    [items, currency, symbol, subtotal, totalItems, addItem, updateQty, removeItem, clearCart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
